/**
 * @file     ComparisonTable.tsx
 * @purpose  Hub comparison table: a real <table> with a caption, column and row
 *           header scopes, a sticky first column, and its own horizontal scroll
 *           (a focusable labelled region) so the page never scrolls sideways on
 *           a phone. Optional preview mode keeps the first rows readable and
 *           routes the rest to an unlock action; that is presentation only and
 *           never a data boundary (gated values must not be passed in at all).
 */
'use client';

import Link from 'next/link';
import Box from '@mui/material/Box';
import { zLayers } from '@/design-system/tokens/zLayers';
import { focusRingSx, hairline, hubColors, hubRadius, transition } from './hubKitStyles';

export interface ComparisonColumn {
  label: string;
  /** Defaults: the first column 'start', the rest 'end' (figures). */
  align?: 'start' | 'end';
}

export interface ComparisonRow {
  /** Stable key; falls back to the row index. */
  key?: string;
  /** Makes the first cell a link to this path. */
  href?: string;
  cells: React.ReactNode[];
}

export interface ComparisonTableProps {
  /** Required: names the table for screen readers and the scroll region. */
  caption: string;
  /** Show the caption above the table. Hidden captions stay in the accessibility tree. */
  captionVisible?: boolean;
  columns: (string | ComparisonColumn)[];
  rows: ComparisonRow[];
  stickyFirstColumn?: boolean;
  /** Width below which the table scrolls inside its container. */
  minWidth?: number;
  /** Keeps this many leading rows readable while later rows render as a blurred preview. */
  previewRowCount?: number;
  locked?: boolean;
  onUnlock?: () => void;
  lockedRowLabel?: string;
  /** Drop the panel frame: hairline rows on the page background (e.g. under a photo row). */
  bare?: boolean;
  /** Keep the column headers for screen readers only, when a visual header sits above the table. */
  columnHeadersHidden?: boolean;
  /** Fixed first-column width (px or responsive px); the other columns share the rest equally. */
  firstColumnWidth?: number | { xs: number; md: number };
}

// String lengths: MUI reads a bare 1 as 100% for width/height and as one spacing unit for margin.
export const visuallyHidden = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  p: 0,
  m: '-1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

const toColumn = (column: string | ComparisonColumn, index: number): Required<ComparisonColumn> =>
  typeof column === 'string'
    ? { label: column, align: index === 0 ? 'start' : 'end' }
    : { label: column.label, align: column.align ?? (index === 0 ? 'start' : 'end') };

export function ComparisonTable({
  caption,
  captionVisible = true,
  columns,
  rows,
  stickyFirstColumn = true,
  minWidth = 560,
  previewRowCount,
  locked = false,
  onUnlock,
  lockedRowLabel = 'See all rows',
  bare = false,
  columnHeadersHidden = false,
  firstColumnWidth,
}: ComparisonTableProps) {
  const cols = columns.map(toColumn);
  const sticky = stickyFirstColumn
    ? { position: 'sticky', left: 0, zIndex: zLayers.content, borderRight: bare ? 'none' : hairline }
    : {};
  const cellPad = { px: { xs: 2, md: 3 }, py: 2 };
  // A bare table sits on the page wash; it never scrolls under its sticky column when minWidth is 0.
  const cellBackground = bare ? 'transparent' : hubColors.surfaceCard;

  return (
    <Box
      role="region"
      aria-label={caption}
      tabIndex={0}
      sx={{
        position: 'relative',
        overflowX: 'auto',
        overflowY: 'hidden',
        border: bare ? 'none' : hairline,
        borderRadius: `${hubRadius.panel}px`,
        backgroundColor: bare ? 'transparent' : hubColors.surfaceCard,
        overscrollBehaviorX: 'contain',
        ...focusRingSx,
      }}
    >
      <Box
        component="table"
        sx={{ width: '100%', minWidth, borderCollapse: 'separate', borderSpacing: 0, fontSize: 15, tableLayout: firstColumnWidth != null ? 'fixed' : 'auto' }}
      >
        {firstColumnWidth != null ? (
          <colgroup>
            <Box component="col" sx={{ width: firstColumnWidth }} />
            {cols.slice(1).map((col, i) => (
              <col key={`${i}-${col.label}`} />
            ))}
          </colgroup>
        ) : null}
        <Box
          component="caption"
          sx={
            captionVisible
              ? { captionSide: 'top', textAlign: 'start', px: { xs: 2, md: 3 }, py: 2, fontSize: 13, lineHeight: 1.5, color: hubColors.textMuted, borderBottom: hairline }
              : visuallyHidden
          }
        >
          {caption}
        </Box>
        <Box component="thead" sx={columnHeadersHidden ? visuallyHidden : undefined}>
          <Box component="tr">
            {cols.map((col, i) => (
              <Box
                key={`${i}-${col.label}`}
                component="th"
                scope="col"
                sx={{
                  ...cellPad,
                  ...(i === 0 ? sticky : {}),
                  py: 2,
                  textAlign: col.align,
                  whiteSpace: 'nowrap',
                  fontSize: 11.5,
                  fontWeight: 500,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: hubColors.textMuted,
                  backgroundColor: hubColors.surfaceSunken,
                  borderBottom: hairline,
                }}
              >
                {col.label}
              </Box>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {rows.map((row, ri) => {
            const rowLocked = locked && previewRowCount != null && ri >= previewRowCount;
            const unlockable = rowLocked && !!onUnlock;
            const isLast = ri === rows.length - 1;
            return (
              <Box
                key={row.key ?? ri}
                component="tr"
                role={unlockable ? 'button' : undefined}
                tabIndex={unlockable ? 0 : undefined}
                aria-label={unlockable ? lockedRowLabel : undefined}
                onClick={unlockable ? onUnlock : undefined}
                onKeyDown={
                  unlockable
                    ? (event: React.KeyboardEvent<HTMLTableRowElement>) => {
                        if (event.key !== 'Enter' && event.key !== ' ') return;
                        event.preventDefault();
                        onUnlock?.();
                      }
                    : undefined
                }
                sx={{
                  cursor: unlockable ? 'pointer' : 'default',
                  '& > *': { transition: transition('background-color') },
                  '&:hover > *': row.href || unlockable ? { backgroundColor: hubColors.backgroundSecondary } : undefined,
                  '&:focus-visible': unlockable ? { outline: `2px solid ${hubColors.interactiveFocusRing}`, outlineOffset: -2 } : undefined,
                }}
              >
                {row.cells.map((cell, ci) => {
                  const col = cols[ci] ?? toColumn('', ci);
                  const content =
                    ci === 0 && row.href && !rowLocked ? (
                      <Box
                        component={Link}
                        href={row.href}
                        sx={{ color: hubColors.link, fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' }, ...focusRingSx }}
                      >
                        {cell}
                      </Box>
                    ) : (
                      cell
                    );
                  return (
                    <Box
                      key={ci}
                      component={ci === 0 ? 'th' : 'td'}
                      scope={ci === 0 ? 'row' : undefined}
                      aria-hidden={rowLocked || undefined}
                      sx={{
                        ...cellPad,
                        ...(ci === 0 ? sticky : {}),
                        textAlign: col.align,
                        fontWeight: ci === 0 ? 600 : 500,
                        color: hubColors.textPrimary,
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: ci === 0 ? 'normal' : 'nowrap',
                        backgroundColor: cellBackground,
                        ...(bare && ri === 0 ? { borderTop: hairline } : {}),
                        borderBottom: isLast && !bare ? 'none' : hairline,
                        filter: rowLocked ? 'blur(6px)' : 'none',
                        userSelect: rowLocked ? 'none' : 'auto',
                      }}
                    >
                      {content}
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
